import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uniq from 'lodash/uniq';
import { useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import classNames from 'classnames';

import { SetScreenType } from 'src/components/Modals/TemplateGallery/index';
import styles from 'src/styles/common/templateGallery.module.scss';
import { TemplateGallerySearchBar } from 'src/components/Modals/TemplateGallery/screens/All/SearchBar';
import { commonSelector, deCommonCurrentPartnerSelector } from 'src/store/commonPart/selector';
import { TABLET_WIDTH } from 'src/config';
import { TemplateGalleryCategories } from 'src/components/Modals/TemplateGallery/screens/All/Categories';
import { useStateForm } from 'src/utils/stateForm';
import { useStateFormValueWatch } from 'src/utils/stateForm/useFormWatch/useStateFormValueWatch';
import {
  TemplateGalleryAvailableCategoriesList,
  TemplateGalleryCampaignTemplatesListType,
} from 'src/models/Modals/templateGallery';
import { useAnyRequestState } from 'src/utils/hooks/useAnyRequestState';
import { apiGetAvailableCampaignTemplateCategories, apiLoadCampaignTemplateList } from 'src/api/organizerTemplate';
import { UiLoader } from 'src/components/UI/loaders';
import { dispatchAnyRequestStart } from 'src/store/anyRequest/helpers';
import { useAnyRequest } from 'src/utils/hooks/useAnyRequest';
import { useNsTHelper } from 'src/utils/i18n';
import { UiButton } from 'src/components/UI/Button';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { TemplateGalleryTemplatesList } from 'src/components/Modals/TemplateGallery/components/TemplatesList';
import { useIdOrRandomString } from 'src/utils/formHelper';

type Props = {
  setScreen: SetScreenType;
  isHidden: boolean;
};

export type FormValues = {
  category: number;
  search: string;
};

export const TemplateGalleryAllScreen: FC<Props> = ({ setScreen, isHidden }) => {
  const tHelper = useNsTHelper('modals.templateGallery', 'all.contact');

  const { innerWidth } = useSelector(commonSelector);
  const isMobile = innerWidth <= TABLET_WIDTH - 1;

  const formProps = useStateForm<FormValues>({
    defaultValues: {
      category: 0,
    },
  });

  const categoryId = useStateFormValueWatch(formProps.getSubscribeProps, 'category');

  const [categoriesModel] = useAnyRequestState<TemplateGalleryAvailableCategoriesList>(null, {
    data: {
      OverridenOrganizationID: window.managedCampaignSettings?.organizationId,
      OverridenUserID: window.managedCampaignSettings?.userId,
    },
    apiFunction: apiGetAvailableCampaignTemplateCategories,
  });

  const templatesUniqueId = useIdOrRandomString();

  const currenPageRef = useRef(0);

  const [model, setModel] = useState<TemplateGalleryCampaignTemplatesListType | null>();

  const response = useAnyRequest<TemplateGalleryCampaignTemplatesListType>(templatesUniqueId, {
    onSuccess: (response) => {
      setModel((prev) => ({
        IsLastBatch: response.IsLastBatch,
        CampaignTemplates:
          currenPageRef.current === 0 || !prev
            ? response.CampaignTemplates
            : [...prev.CampaignTemplates, ...response.CampaignTemplates],
      }));
    },
  });

  const getTemplates = useCallback(
    (PageIndex = 0) => {
      currenPageRef.current = PageIndex;
      if (PageIndex === 0) {
        setModel(null);
      }

      dispatchAnyRequestStart({
        data: {
          CategoryID: formProps.getValue('category') || undefined,
          Term: formProps.getValue('search'),
          PageIndex,
          PageSize: 10,
          OverridenOrganizationID: window.managedCampaignSettings?.organizationId,
          OverridenUserID: window.managedCampaignSettings?.userId,
        },
        apiFunction: apiLoadCampaignTemplateList,
        uniqueId: templatesUniqueId,
      });
    },
    [formProps, templatesUniqueId]
  );

  const getTemplatesInfinity = () => {
    if (response.loading || model?.IsLastBatch || !model) {
      return;
    }

    getTemplates(currenPageRef.current + 1);
  };

  useEffect(() => {
    getTemplates();
  }, [categoryId, getTemplates, templatesUniqueId]);

  const categoryLabel = useMemo(
    () => categoriesModel?.Categories?.find((item) => item.ID === categoryId)?.Title,
    [categoriesModel?.Categories, categoryId]
  );

  const templatesNames = useMemo(
    () => (model ? uniq(model.CampaignTemplates.map(({ Name }) => Name)).filter(Boolean) : []),
    [model]
  );

  const { ContactEmail, HideMailToForOrganizerTemplateGalleryContactUs } = useSelector(deCommonCurrentPartnerSelector);

  const letUsKnowProps = useMemo(
    () => ({
      className: styles.feedbackLink,
      children: (
        <>
          &nbsp;
          {tHelper('letUsKnow')}
        </>
      ),
    }),
    [tHelper]
  );

  return categoriesModel ? (
    <div className={classNames(styles.content, isHidden && styles.hidden)}>
      <TemplateGallerySearchBar
        isMobile={isMobile}
        formProps={formProps}
        templatesNames={templatesNames}
        getTemplates={getTemplates}
      />
      {categoriesModel?.Categories && (
        <TemplateGalleryCategories isMobile={isMobile} formProps={formProps} categories={categoriesModel.Categories} />
      )}

      <div className={styles.templatesList}>
        {categoriesModel?.Categories && <div className={styles.categoryTitle}>{categoryLabel}</div>}

        <div className={styles.templatesWrapper}>
          {model ? (
            <InfiniteScroll
              className={styles.templates}
              loadMore={getTemplatesInfinity}
              hasMore={!model.IsLastBatch}
              useWindow={false}
              threshold={150}
              initialLoad={false}
            >
              <TemplateGalleryTemplatesList setScreen={setScreen} items={model.CampaignTemplates} />
              {response.loading && <UiLoader className={styles.loader} centered />}
            </InfiniteScroll>
          ) : (
            <div className={`${styles.templates} ${styles.loading}`}>
              <UiLoader className={styles.loader} centered />
            </div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <hr className={styles.divider} />

        <div className="text-center">
          <span>{tHelper('question')}</span>
          {HideMailToForOrganizerTemplateGalleryContactUs ? (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <UiButton tag="a" id="11bc3c2f-c942-41ec-900d-a2f547863e4f" {...letUsKnowProps} />
          ) : (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <UiInnerNotSPALink href={`mailto:${ContactEmail}`} {...letUsKnowProps} />
          )}
        </div>
      </div>
    </div>
  ) : (
    <UiLoader centered />
  );
};
